// Consolidated org health/status (admin JWT). Shape mirrors the API exactly:
//   - OrgHealthReport → api/src/services/health-service.ts
//   - the route       → api/src/http/routes/admin.ts (GET /admin/health)
//
// Surfaces the two signals the usage report doesn't carry — the org's account
// status and the control-plane→relay link reachability — alongside a usage echo.
// `relay.reachable` is a 3-state: true (reachable) / false (configured but down) /
// null (not monitored). Success bodies are Zod-validated so envelope drift fails
// loud as a ContractError.
import { z } from "zod";
import { apiFetch } from "./client";

export type OrgAccountStatus = "active" | "suspended" | "deleted";

export interface OrgHealth {
  org: { name: string; status: OrgAccountStatus; tier: string };
  channels: { used: number; limit: number; remaining: number };
  agents: { used: number | null; limit: number; remaining: number | null };
  subscription: { status: string | null; currentPeriodEnd: string | null };
  /** Control-plane→relay reachability. null = not monitored (relay link off). */
  relay: { reachable: boolean | null };
}

const healthSchema = z.object({
  org: z.object({
    name: z.string(),
    status: z.enum(["active", "suspended", "deleted"]),
    tier: z.string(),
  }),
  channels: z.object({ used: z.number(), limit: z.number(), remaining: z.number() }),
  agents: z.object({ used: z.number().nullable(), limit: z.number(), remaining: z.number().nullable() }),
  subscription: z.object({ status: z.string().nullable(), currentPeriodEnd: z.string().nullable() }),
  relay: z.object({ reachable: z.boolean().nullable() }),
});

/** Consolidated health/status for the caller's org. */
export function getHealth(signal?: AbortSignal): Promise<OrgHealth> {
  return apiFetch("/admin/health", { auth: true, signal, schema: healthSchema });
}
